import { Injectable } from '@angular/core';
import { getCssVariable } from './css-variables.lib';

/**
 * Service for retrieving CSS custom property (CSS variable) values at runtime.
 *
 * @remarks
 * This service provides a wrapper around the {@link getCssVariable} utility function,
 * enabling dependency injection of CSS variable access throughout the application.
 * Automatically handles browser compatibility by detecting CSS variable support
 * and falling back to DOM parsing for older browsers.
 *
 * **Usage**: Import {@link CssVariablesModule} to make this service available,
 * then inject it into components or other services using Angular's inject() function.
 *
 * @example
 *
 * ```typescript
 * import { inject } from '@angular/core';
 * import { CssVariablesService } from '@backbase/css-variables-lib';
 *
 * export class MyComponent {
 *   private cssVariables = inject(CssVariablesService);
 *
 *   async ngOnInit() {
 *     const primaryColor = await this.cssVariables.getCssVariable('--primary-color');
 *     console.log('Primary color:', primaryColor);
 *   }
 * }
 * ```
 */
@Injectable()
export class CssVariablesService {
  /**
   * Retrieves the value of a CSS custom property at runtime.
   *
   * @param name - The CSS variable name (e.g., `--primary-color`, `--breakpoint-tablet`).
   * @param elementName - The selector of the element to query for scoped variables (defaults to `:root` for global variables).
   * @param searchEverywhere - If true, forces DOM parsing on all browsers; defaults to false for optimal performance.
   * @returns A promise resolving to the CSS variable value, or undefined if the variable is not found.
   *
   * @remarks
   * This method delegates to the {@link getCssVariable} utility function.
   * Returns a promise to support asynchronous CSS parsing on older browsers.
   * For global CSS variables defined in `:root`, only the `name` parameter is required.
   * For scoped variables, provide the element selector as the second parameter.
   *
   * @example
   *
   * ```typescript
   * // Get a global CSS variable
   * const bgColor = await this.cssVariables.getCssVariable('--background-color');
   *
   * // Get a scoped CSS variable from a specific element class
   * const sidebarWidth = await this.cssVariables.getCssVariable('--sidebar-width', '.sidebar-container');
   * ```
   */
  getCssVariable(name: string, elementName: string = ':root', searchEverywhere = false): Promise<string | undefined> {
    return getCssVariable(name, elementName, searchEverywhere);
  }
}
